import { useState, useCallback } from "react";
import Modal from "@/components/shared/Modal";
import type { Project } from "@/pages/projects/hooks/useProject";
import {
  generateInstallments,
  recalculateDownstreamAmounts,
  recalculateDownstreamDueDates,
} from "../hooks/generateInstallments";
import type { InstallmentDraft } from "../hooks/generateInstallments";
import { useCreateContractValidation } from "../hooks/useCreateContractValidation";
import type { CreateContractStepOneData } from "../hooks/useCreateContractValidation";
import { useCreateContract } from "../hooks/useCreateContract";
import CreateContractStepOne from "./CreateContractStepOne";
import CreateContractStepTwo from "./CreateContractStepTwo";

interface CreateContractWizardProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}

const INITIAL_STEP_ONE: CreateContractStepOneData = {
  name: "",
  totalValue: "",
  numberOfInstallments: "",
  startDate: "",
  userIds: [],
};

export default function CreateContractWizard({
  project,
  isOpen,
  onClose,
}: CreateContractWizardProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [stepOne, setStepOne] = useState<CreateContractStepOneData>(INITIAL_STEP_ONE);
  const [installments, setInstallments] = useState<InstallmentDraft[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { errors, validateStepOne, clearErrors } = useCreateContractValidation();
  const { createContract, loading } = useCreateContract(project.id);

  const reset = useCallback(() => {
    setStep(1);
    setStepOne(INITIAL_STEP_ONE);
    setInstallments([]);
    setSubmitError(null);
    clearErrors();
  }, [clearErrors]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleStepOneChange = (
    field: keyof CreateContractStepOneData,
    value: string | string[]
  ) => {
    setStepOne((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerate = () => {
    if (!validateStepOne(stepOne)) return;

    const total = parseFloat(stepOne.totalValue);
    const count = parseInt(stepOne.numberOfInstallments, 10);
    setInstallments(generateInstallments(total, count, stepOne.startDate));
    setStep(2);
    setSubmitError(null);
  };

  const handleInstallmentChange = (
    index: number,
    field: "amount" | "dueDate",
    value: string
  ) => {
    const total = parseFloat(stepOne.totalValue) || 0;

    setInstallments((prev) => {
      const updated = prev.map((item, i) => {
        if (i !== index) return item;
        if (field === "amount") {
          return { ...item, amount: parseFloat(value) || 0 };
        }
        return { ...item, dueDate: value };
      });

      if (field === "amount") {
        return recalculateDownstreamAmounts(updated, index, total);
      }
      return recalculateDownstreamDueDates(updated, index);
    });
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      await createContract({
        projectId: project.id,
        name: stepOne.name.trim(),
        totalValue: stepOne.totalValue,
        installments,
        userIds: stepOne.userIds,
      });
      handleClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erro ao criar contrato";
      setSubmitError(message);
    }
  };

  const totalValue = parseFloat(stepOne.totalValue) || 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Novo Contrato"
      maxWidth="max-w-2xl"
      closeOnOverlayClick
    >
      <p className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400 mb-6">
        {step} de 2
      </p>

      {step === 1 ? (
        <CreateContractStepOne
          project={project}
          data={stepOne}
          errors={errors}
          loading={loading}
          onChange={handleStepOneChange}
          onGenerate={handleGenerate}
          onCancel={handleClose}
        />
      ) : (
        <CreateContractStepTwo
          installments={installments}
          totalValue={totalValue}
          loading={loading}
          errorMessage={submitError ?? undefined}
          onChangeInstallment={handleInstallmentChange}
          onBack={() => setStep(1)}
          onSubmit={handleSubmit}
        />
      )}
    </Modal>
  );
}
